import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useState,
} from "react";
import { TutorialHintDef, TutorialTourDef } from "./types";

// Session dismissals survive page reloads within the same tab but are cleared
// when the browser (or tab) is closed — used for noPersist hints, which are the
// only hints that still live in browser storage.
//
// Everything else is persisted per user on the server (see TutorialHydrator).
// The previous localStorage store was removed deliberately: it was keyed
// globally rather than per user, so "show once ever" really meant "show once
// per browser", and two accounts sharing a machine shared dismissal state —
// silently suppressing the second user's onboarding entirely.
//
// The key is namespaced by user id for the same reason. sessionStorage is
// per-tab but not per-user, and it outlives any React remount, so a single
// global key reproduced that leak in miniature: sign out, sign in as someone
// else in the same tab, and the incoming user inherited the previous user's
// noPersist dismissals — which in practice meant no "Set up this workspace"
// prompt in their brand new IDA workspace. Namespacing keeps the
// survive-a-reload behaviour these hints are written for without letting it
// cross an account boundary.
const SESSION_KEY_PREFIX = "mm_tutorial_dismissed_session";

function sessionKey(userId: string): string {
  return `${SESSION_KEY_PREFIX}::${userId}`;
}

function loadSessionDismissed(userId: string | null): string[] {
  if (!userId) return [];
  try {
    const raw = sessionStorage.getItem(sessionKey(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function appendToSession(userId: string | null, id: string) {
  // Unreachable in practice: a dismissal requires an active hint, which
  // requires hydrated, which is only set together with the user id.
  if (!userId) return;
  try {
    const ids = loadSessionDismissed(userId);
    if (!ids.includes(id)) {
      ids.push(id);
      sessionStorage.setItem(sessionKey(userId), JSON.stringify(ids));
    }
  } catch {}
}

// ─── Dismissal keys ──────────────────────────────────────────────────────────

/**
 * A dismissal is identified by the hint plus the workspace it applies to.
 * User-scoped hints use the "-" sentinel so they can never collide with a
 * workspace-scoped dismissal of the same hint id.
 */
export function dismissalKey(
  hintId: string,
  workspaceId?: string | null,
): string {
  return `${hintId}::${workspaceId ?? "-"}`;
}

function keyForHint(
  hint: TutorialHintDef,
  currentWorkspaceId: string | null,
): string {
  return hint.scope === "workspace"
    ? dismissalKey(hint.id, currentWorkspaceId)
    : dismissalKey(hint.id, null);
}

// ─── Reducer ────────────────────────────────────────────────────────────────

interface TutorialState {
  activeHint: TutorialHintDef | null;
  activeTour: TutorialTourDef | null;
  activeTourStepIndex: number;
  dismissedKeys: string[];
  /**
   * False until the server's dismissal list has loaded. Nothing displays while
   * false — a slow or failed load errs toward showing no hint rather than
   * replaying one the user already dismissed on another device.
   */
  hydrated: boolean;
  /** Workspace the user is currently in, used to resolve workspace-scoped keys. */
  currentWorkspaceId: string | null;
  /**
   * Who the loaded dismissal state belongs to. Set by HYDRATE and used to
   * namespace the per-tab noPersist store so it cannot cross an account.
   */
  userId: string | null;
}

export type TutorialAction =
  | { type: "SHOW_HINT"; hint: TutorialHintDef }
  | { type: "HIDE_HINT"; id: string }
  | { type: "DISMISS_ACTIVE" }
  | { type: "START_TOUR"; tour: TutorialTourDef }
  | { type: "NEXT_STEP" }
  | { type: "RESET_DISMISSED" }
  | { type: "BEGIN_HYDRATION" }
  | { type: "HYDRATE"; keys: string[]; userId: string }
  | { type: "SET_WORKSPACE"; workspaceId: string | null };

function reducer(state: TutorialState, action: TutorialAction): TutorialState {
  switch (action.type) {
    case "SHOW_HINT": {
      // Fail closed: never show anything before the server state has arrived.
      if (!state.hydrated) return state;
      // A tour's own hints take exclusive control
      if (state.activeTour) return state;
      if (
        state.dismissedKeys.includes(
          keyForHint(action.hint, state.currentWorkspaceId),
        )
      )
        return state;
      if (state.activeHint?.id === action.hint.id) return state;
      return { ...state, activeHint: action.hint };
    }

    case "HIDE_HINT": {
      // Only hide if this is the active contextual hint (not a tour step)
      if (state.activeTour || state.activeHint?.id !== action.id) return state;
      return { ...state, activeHint: null };
    }

    case "DISMISS_ACTIVE": {
      const next = [...state.dismissedKeys];
      const hint = state.activeHint;
      const tour = state.activeTour;

      const addKey = (key: string) => {
        if (!next.includes(key)) next.push(key);
      };

      // Server persistence is the caller's job (see useTutorialActions). The
      // reducer only owns what is on screen and what this tab has already
      // dismissed, so it stays pure and there is no outbound queue to lose.
      if (hint) {
        addKey(keyForHint(hint, state.currentWorkspaceId));
        if (hint.noPersist) {
          // noPersist hints stay in sessionStorage (cleared on browser close)
          // so they reappear in a new browser session and never reach the server.
          appendToSession(state.userId, hint.id);
        }
      }
      if (tour) addKey(dismissalKey(tour.id, null));

      return {
        ...state,
        activeHint: null,
        activeTour: null,
        activeTourStepIndex: 0,
        dismissedKeys: next,
      };
    }

    case "START_TOUR": {
      if (!state.hydrated) return state;
      if (
        state.dismissedKeys.includes(dismissalKey(action.tour.id, null)) ||
        !action.tour.steps.length
      )
        return state;
      return {
        ...state,
        activeTour: action.tour,
        activeTourStepIndex: 0,
        activeHint: action.tour.steps[0],
      };
    }

    case "NEXT_STEP": {
      if (!state.activeTour) return state;
      const nextIdx = state.activeTourStepIndex + 1;
      if (nextIdx < state.activeTour.steps.length) {
        return {
          ...state,
          activeTourStepIndex: nextIdx,
          activeHint: state.activeTour.steps[nextIdx],
        };
      }
      // Tour complete — mark it dismissed. Persisting that is the caller's
      // job (see useTutorialActions.nextStep).
      const tourKey = dismissalKey(state.activeTour.id, null);
      return {
        ...state,
        activeTour: null,
        activeTourStepIndex: 0,
        activeHint: null,
        dismissedKeys: state.dismissedKeys.includes(tourKey)
          ? state.dismissedKeys
          : [...state.dismissedKeys, tourKey],
      };
    }

    case "RESET_DISMISSED": {
      try {
        if (state.userId) sessionStorage.removeItem(sessionKey(state.userId));
      } catch {}
      // The server-side clear is issued by useTutorial().resetAllDismissed
      // before this dispatch.
      return { ...state, dismissedKeys: [] };
    }

    case "BEGIN_HYDRATION": {
      // A fresh mount means a fresh authenticated tree, so start from nothing
      // and let HYDRATE re-establish state for whoever is signed in now.
      //
      // dismissedKeys goes fully empty rather than being reseeded from the
      // per-tab store, because the incoming user id isn't known yet. Nothing
      // can display in the meantime — hydrated is false — and HYDRATE folds in
      // that user's own session dismissals once the identity resolves.
      return {
        ...state,
        activeHint: null,
        activeTour: null,
        activeTourStepIndex: 0,
        dismissedKeys: [],
        hydrated: false,
        userId: null,
      };
    }

    case "HYDRATE": {
      // Merge rather than replace so a dismissal made while the fetch was in
      // flight isn't lost, and fold in this session's noPersist dismissals.
      const merged = new Set([
        ...state.dismissedKeys,
        ...action.keys,
        ...loadSessionDismissed(action.userId).map((id) =>
          dismissalKey(id, null),
        ),
      ]);
      return {
        ...state,
        dismissedKeys: [...merged],
        hydrated: true,
        userId: action.userId,
      };
    }

    case "SET_WORKSPACE": {
      if (state.currentWorkspaceId === action.workspaceId) return state;
      return { ...state, currentWorkspaceId: action.workspaceId };
    }

    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

export interface TutorialContextValue {
  state: TutorialState;
  dispatch: React.Dispatch<TutorialAction>;
  registerTarget: (id: string, el: HTMLElement | null) => void;
  getTargetElement: (id: string) => HTMLElement | null;
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export const TutorialProviderBase: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    activeHint: null,
    activeTour: null,
    activeTourStepIndex: 0,
    // Empty until HYDRATE: the per-tab noPersist store is namespaced by user
    // id, which isn't known until the hydrator resolves it.
    dismissedKeys: [],
    hydrated: false,
    currentWorkspaceId: null,
    userId: null,
  }));

  // Reactive registry: using state (not a plain ref) so TutorialOverlay
  // re-renders whenever a target element mounts or unmounts — critical for
  // the mobile sidebar which conditionally mounts/unmounts its nav items.
  const [registry, setRegistry] = useState<Map<string, HTMLElement>>(
    () => new Map(),
  );

  const registerTarget = useCallback((id: string, el: HTMLElement | null) => {
    setRegistry((prev) => {
      const next = new Map(prev);
      if (el) {
        next.set(id, el);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const getTargetElement = useCallback(
    (id: string) => registry.get(id) ?? null,
    [registry],
  );

  return (
    <TutorialContext.Provider
      value={{ state, dispatch, registerTarget, getTargetElement }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useTutorialContext(): TutorialContextValue {
  const ctx = useContext(TutorialContext);
  if (!ctx)
    throw new Error("useTutorialContext must be used inside TutorialProvider");
  return ctx;
}
